import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client'
import { useEffect, useState } from 'react'
import {
  Search,
  Loader2,
  Plus,
  ClipboardCheck,
  ChevronDown,
  Link2,
} from 'lucide-react'
import { format } from 'date-fns'
import { nb } from 'date-fns/locale'
import loftIcon from '@/assets/icons/ndt-icons/loft_nolabel.svg'
import etIcon from '@/assets/icons/ndt-icons/et_nolabel.svg'
import mtIcon from '@/assets/icons/ndt-icons/mt_nolabel.svg'
import ptIcon from '@/assets/icons/ndt-icons/pt_nolabel.svg'
import vtIcon from '@/assets/icons/ndt-icons/VT_nolabel.svg'
import ltIcon from '@/assets/icons/ndt-icons/lt_nolabel.svg'
import utIcon from '@/assets/icons/ndt-icons/ut_nolabel.svg'
import rtIcon from '@/assets/icons/ndt-icons/rt_nolabel.svg'
import threadIcon from '@/assets/icons/ndt-icons/thread_nolabel.svg'
import wrenchIcon from '@/assets/icons/ndt-icons/wrench_nolabel.svg'
import { EquipmentDetailsModal, EquipmentCreationModal } from '@/components/inspection'
import { ndtFormPath, setNdtExitPath } from '@/utils/ndtNavigation'
import {
  STMIDBEquipmentService,
  STMIDB_WLL_COMPANY_ID,
  STMIDB_WLL_COMPANY_NAME,
} from '@/services/api/stmidbEquipmentService'

const NDT_SHORTCUTS = [
  { name: 'Løft', label: 'Løft', url: loftIcon, kind: 'loft' as const },
  { name: 'ET', label: 'ET', url: etIcon },
  { name: 'MPI', label: 'MPI', url: mtIcon },
  { name: 'PT', label: 'PT', url: ptIcon },
  { name: 'VT', label: 'VT', url: vtIcon },
  { name: 'LT', label: 'LT', url: ltIcon },
  { name: 'UT', label: 'UT', url: utIcon },
  { name: 'RT', label: 'RT', url: rtIcon },
  { name: 'Thread', label: 'Gjenger', url: threadIcon },
  { name: 'SR', label: 'Service', url: wrenchIcon },
] as const

const SHOW_RECENT_INSPECTIONS = false
const SHOW_WELCOME_HEADER = false

interface SearchEquipmentItem {
  id: string
  asset_number: string
  serial_number: string
  part_number: string | null
  owner_id: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  name: string
  company_id: string
  company_name: string
}

interface SearchResult extends SearchEquipmentItem {
  sub_equipment: SearchEquipmentItem[]
}

const formatNdtDate = (value: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'dd.MM.yyyy')
}

const isNdtExpired = (item: SearchEquipmentItem) => {
  const expirySource = item.next_ndt_date || item.last_ndt_date
  if (!expirySource) return false
  const expiry = new Date(expirySource)
  if (Number.isNaN(expiry.getTime())) return false
  if (!item.next_ndt_date && item.last_ndt_date) {
    expiry.setFullYear(expiry.getFullYear() + 4)
  }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  expiry.setHours(0, 0, 0, 0)
  return expiry < today
}

const EquipmentSearchMeta = ({ item }: { item: SearchEquipmentItem }) => {
  const ndtExpired = isNdtExpired(item)
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-slate-400">
      {item.owner_id && (
        <span><span className="text-slate-500">Owner_ID:</span> {item.owner_id}</span>
      )}
      <span><span className="text-slate-500">SN:</span> {item.serial_number || '—'}</span>
      {item.part_number && (
        <span><span className="text-slate-500">PN:</span> {item.part_number}</span>
      )}
      <span className={ndtExpired ? 'text-red-500 font-semibold' : undefined}>
        <span className={ndtExpired ? undefined : 'text-slate-500'}>NDT-dato:</span>{' '}
        {formatNdtDate(item.last_ndt_date)}
      </span>
    </div>
  )
}

interface RecentInspection {
  id: string
  created_at: string
  inspection_status: string | null
  equipment_name: string
  equipment_serial: string
  company_name: string
  storage_path: string
  file_name: string
  bucket: string
}

interface InspectorStats {
  recentInspections: RecentInspection[]
  recentNdtReports: RecentInspection[]
}

const STATUS_COLOR: Record<string, { bg: string; text: string }> = {
  OK:  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  C:   { bg: 'bg-amber-100',   text: 'text-amber-700' },
  M:   { bg: 'bg-blue-100',    text: 'text-blue-700' },
  MO:  { bg: 'bg-orange-100',  text: 'text-orange-700' },
  RC:  { bg: 'bg-red-100',     text: 'text-red-700' },
  NC:  { bg: 'bg-red-100',     text: 'text-red-700' },
  EXP: { bg: 'bg-slate-100',   text: 'text-slate-500' },
}

interface DocColumnProps {
  title: string
  rows: RecentInspection[]
  emptyText: string
  openingDocId: string | null
  onOpen: (doc: RecentInspection) => void
}

const DocColumn = ({ title, rows, emptyText, openingDocId, onOpen }: DocColumnProps) => (
  <div className="flex-1 min-w-0 flex flex-col">
    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 px-4 py-2.5 border-b border-slate-100">
      {title}
    </p>
    {rows.length === 0 ? (
      <div className="flex-1 flex items-center justify-center py-6">
        <p className="text-xs text-slate-400">{emptyText}</p>
      </div>
    ) : (
      <div className="divide-y divide-slate-100">
        {rows.map((insp) => {
          const statusStyle = STATUS_COLOR[insp.inspection_status ?? ''] ?? { bg: 'bg-slate-100', text: 'text-slate-500' }
          const isOpening = openingDocId === insp.id
          return (
            <button
              key={insp.id}
              onClick={() => onOpen(insp)}
              disabled={isOpening}
              className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left disabled:opacity-60 group"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 group-hover:text-[#1E3A5F] truncate transition-colors leading-tight">
                  {insp.company_name && insp.company_name !== '—'
                    ? `${insp.company_name} – ${insp.equipment_name}`
                    : insp.equipment_name}
                </p>
                <p className="text-xs text-slate-400 truncate mt-0.5">
                  {format(new Date(insp.created_at), 'd. MMM yyyy', { locale: nb })}
                  <span className="mx-1.5 text-slate-300">·</span>
                  SN: {insp.equipment_serial}
                </p>
              </div>
              {isOpening ? (
                <Loader2 size={13} className="shrink-0 mt-1 animate-spin text-slate-400" />
              ) : insp.inspection_status ? (
                <span className={`shrink-0 mt-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                  {insp.inspection_status}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    )}
  </div>
)

interface SplitRecentProps {
  inspections: RecentInspection[]
  ndtReports: RecentInspection[]
  expanded: boolean
  onToggle: () => void
  openingDocId: string | null
  onOpen: (doc: RecentInspection) => void
}

const SplitRecentTable = ({ inspections, ndtReports, expanded, onToggle, openingDocId, onOpen }: SplitRecentProps) => (
  <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full px-5 py-3 flex items-center gap-2 hover:bg-slate-50 transition-colors"
    >
      <ClipboardCheck size={14} className="text-[#1E3A5F] shrink-0" />
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 flex-1 text-left">
        Siste inspeksjoner
      </p>
      <ChevronDown
        size={15}
        className={`text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
      />
    </button>

    {expanded && (
      <div className="flex border-t border-slate-100 divide-x divide-slate-100">
        <DocColumn
          title="Periodisk kontroll"
          rows={inspections}
          emptyText="Ingen kontrollkort ennå"
          openingDocId={openingDocId}
          onOpen={onOpen}
        />
        <DocColumn
          title="NDT-rapporter"
          rows={ndtReports}
          emptyText="Ingen NDT-rapporter ennå"
          openingDocId={openingDocId}
          onOpen={onOpen}
        />
      </div>
    )}
  </div>
)

const Dashboard = () => {
  const navigate = useNavigate()
  const [firstName, setFirstName]     = useState<string>('')
  const [userRole, setUserRole]       = useState<string | null>(null)
  const [userId, setUserId]           = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [inspectorStats, setInspectorStats] = useState<InspectorStats>({
    recentInspections: [],
    recentNdtReports: [],
  })
  const [recentExpanded, setRecentExpanded]    = useState(false)
  const [openingDocId, setOpeningDocId]        = useState<string | null>(null)
  const [selectedEquipment, setSelectedEquipment] = useState<SearchEquipmentItem | null>(null)
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false)

  useEffect(() => {
    fetchUserProfile()
  }, [])

  useEffect(() => {
    if (SHOW_RECENT_INSPECTIONS && userRole === 'inspector' && userId) fetchInspectorStats(userId)
  }, [userRole, userId])

  const fetchUserProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      setUserId(user.id)
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, role')
        .eq('id', user.id)
        .single()

      if (profile?.full_name) setFirstName(profile.full_name.split(' ')[0])
      setUserRole(profile?.role ?? 'user')
    }
  }

  const fetchInspectorStats = async (uid: string) => {
    const docSelect = `
      id,
      created_at,
      inspection_status,
      storage_path,
      file_name,
      equipment_v2!equipment_id (
        name,
        serial_number,
        companies ( name )
      )
    `

    const [recentRes, ndtRes] = await Promise.all([
      supabase
        .from('equipment_documents')
        .select(docSelect)
        .eq('uploaded_by', uid)
        .eq('document_type', 'control_card')
        .order('created_at', { ascending: false })
        .limit(10),

      supabase
        .from('ndt_reports')
        .select(`
          id,
          created_at,
          order_number,
          unit_id,
          test_type,
          status,
          file_path,
          companies ( name )
        `)
        .eq('created_by', uid)
        .order('created_at', { ascending: false })
        .limit(10),
    ])

    const mapControlCards = (rows: any[]): RecentInspection[] =>
      rows.map((row: any) => ({
        id: row.id,
        created_at: row.created_at,
        inspection_status: row.inspection_status ?? null,
        equipment_name: row.equipment_v2?.name ?? '—',
        equipment_serial: row.equipment_v2?.serial_number ?? '—',
        company_name: row.equipment_v2?.companies?.name ?? '—',
        storage_path: row.storage_path ?? '',
        file_name: row.file_name ?? '',
        bucket: 'documentation',
      }))

    const mapNdtReports = (rows: any[]): RecentInspection[] =>
      rows.map((row: any) => ({
        id: row.id,
        created_at: row.created_at,
        inspection_status: row.status ?? null,
        equipment_name: row.order_number ?? '—',
        equipment_serial: row.unit_id ?? '—',
        company_name: (row.companies as any)?.name ?? '—',
        storage_path: row.file_path ?? '',
        file_name: row.order_number ?? '',
        bucket: 'reports',
      }))

    setInspectorStats({
      recentInspections: mapControlCards(recentRes.data ?? []),
      recentNdtReports: mapNdtReports(ndtRes.data ?? []),
    })
  }

  const handleNdtSelection = (icon: (typeof NDT_SHORTCUTS)[number]) => {
    if ('kind' in icon && icon.kind === 'loft') {
      sessionStorage.setItem('inspectionType', 'loft')
      sessionStorage.setItem('loftType', 'sakkyndig')
      sessionStorage.setItem('loftExitPath', '/dashboard')
      sessionStorage.removeItem('ndtType')
      navigate('/company')
      return
    }

    sessionStorage.setItem('inspectionType', 'ndt')
    sessionStorage.setItem('ndtType', icon.name)
    setNdtExitPath('/dashboard')

    const path = ndtFormPath(icon.name)
    navigate(path ?? '/company')
  }

  const openDocument = async (doc: RecentInspection) => {
    if (!doc.storage_path) return
    setOpeningDocId(doc.id)
    try {
      const realPath = doc.storage_path.includes('#')
        ? doc.storage_path.split('#')[0]
        : doc.storage_path
      const { data, error } = await supabase.storage
        .from(doc.bucket || 'documentation')
        .createSignedUrl(realPath, 3600)
      if (!error && data?.signedUrl) {
        window.open(data.signedUrl, '_blank')
      }
    } finally {
      setOpeningDocId(null)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setIsSearching(true)
    setHasSearched(true)
    try {
      const searchTerm = `%${searchQuery.trim()}%`
      const { data, error } = await supabase
        .from('equipment_v2')
        .select(`
          id,
          asset_number,
          serial_number,
          part_number,
          owner_id,
          last_ndt_date,
          next_ndt_date,
          name,
          company_id,
          companies!company_id (name)
        `)
        .or(`name.ilike.${searchTerm},serial_number.ilike.${searchTerm},asset_number.ilike.${searchTerm},owner_id.ilike.${searchTerm}`)
        .limit(5)

      if (error) { setSearchResults([]); return }

      const mapped: SearchResult[] = (data || []).map((item) => ({
        id: item.id,
        asset_number: item.asset_number || '',
        serial_number: item.serial_number || '',
        part_number: item.part_number || null,
        owner_id: item.owner_id || null,
        last_ndt_date: item.last_ndt_date || null,
        next_ndt_date: item.next_ndt_date || null,
        name: item.name || '',
        company_id: item.company_id,
        company_name: (item.companies as { name?: string } | null)?.name ?? 'Ukjent selskap',
        sub_equipment: [],
      }))

      if (mapped.length > 0) {
        const { data: rels } = await supabase
          .from('equipment_relationship_v2')
          .select(`
            main_equipment_id,
            sub_equipment:equipment_v2!equipment_relationship_v2_sub_equipment_id_fkey(
              id,
              name,
              asset_number,
              serial_number,
              part_number,
              owner_id,
              last_ndt_date,
              next_ndt_date,
              company_id
            )
          `)
          .in('main_equipment_id', mapped.map((item) => item.id))

        const subsByParent = new Map<string, SearchEquipmentItem[]>()
        for (const rel of rels || []) {
          const sub = (Array.isArray(rel.sub_equipment) ? rel.sub_equipment[0] : rel.sub_equipment) as {
            id: string
            name: string | null
            asset_number: string | null
            serial_number: string | null
            part_number: string | null
            owner_id: string | null
            last_ndt_date: string | null
            next_ndt_date: string | null
            company_id: string
          } | null
          if (!sub?.id) continue
          const parent = mapped.find((item) => item.id === rel.main_equipment_id)
          const list = subsByParent.get(rel.main_equipment_id) ?? []
          list.push({
            id: sub.id,
            asset_number: sub.asset_number || '',
            serial_number: sub.serial_number || '',
            part_number: sub.part_number || null,
            owner_id: sub.owner_id || null,
            last_ndt_date: sub.last_ndt_date || null,
            next_ndt_date: sub.next_ndt_date || null,
            name: sub.name || '',
            company_id: sub.company_id || parent?.company_id || '',
            company_name: parent?.company_name ?? 'Ukjent selskap',
          })
          subsByParent.set(rel.main_equipment_id, list)
        }

        for (const item of mapped) {
          item.sub_equipment = subsByParent.get(item.id) ?? []
        }
      }

      const slbHits = await STMIDBEquipmentService.searchReportEquipment(searchQuery.trim(), 5)
        .catch((err) => {
          console.error('SLB search failed:', err)
          return []
        })
      for (const hit of slbHits) {
        mapped.push({
          id: hit.id,
          asset_number: hit.asset_number || '',
          serial_number: hit.serial_number || '',
          part_number: hit.part_number,
          owner_id: hit.equipment_code,
          last_ndt_date: null,
          next_ndt_date: null,
          name: hit.description || hit.equipment_type || '',
          company_id: STMIDB_WLL_COMPANY_ID,
          company_name: STMIDB_WLL_COMPANY_NAME,
          sub_equipment: [],
        })
      }

      setSearchResults(mapped)
    } catch {
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleResultClick = (result: SearchEquipmentItem) => {
    sessionStorage.setItem('selectedCompanyId', result.company_id)
    setSelectedEquipment(result)
  }

  const isInspector = userRole === 'inspector'

  return (
    <div
      className="h-full flex flex-col px-6 py-5 gap-4"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="max-w-5xl w-full mx-auto flex flex-col gap-4 h-full min-h-0">

        {/* Page heading */}
        {SHOW_WELCOME_HEADER && (
          <div className="shrink-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-0.5">
              Oversikt
            </p>
            <h1 className="text-xl font-semibold text-slate-800">
              Velkommen tilbake,{' '}
              <span style={{ color: 'var(--color-primary)' }}>
                {firstName || 'Bruker'}
              </span>
            </h1>
          </div>
        )}

        {/* ── NDT shortcuts ──────────────────────────────────────── */}
        <div className="grid grid-cols-4 gap-x-4 gap-y-6 shrink-0">
          {NDT_SHORTCUTS.map((icon) => (
            <button
              key={icon.name}
              onClick={() => handleNdtSelection(icon)}
              className="flex flex-col items-center justify-center gap-2 py-2 transition-transform active:scale-95 group"
            >
              <img
                src={icon.url}
                alt={icon.label}
                className="h-14 sm:h-16 w-auto max-w-[70%] object-contain"
              />
              <span className="text-sm font-medium text-slate-600 group-hover:text-[#1E3A5F] transition-colors">
                {icon.label}
              </span>
            </button>
          ))}
        </div>

        {/* ── Inspector recent inspections ───────────────────────── */}
        {SHOW_RECENT_INSPECTIONS && isInspector && (
          <SplitRecentTable
            inspections={inspectorStats.recentInspections}
            ndtReports={inspectorStats.recentNdtReports}
            expanded={recentExpanded}
            onToggle={() => setRecentExpanded(v => !v)}
            openingDocId={openingDocId}
            onOpen={openDocument}
          />
        )}

        {/* ── Equipment search ───────────────────────────────────── */}
        <div className="flex-1 min-h-0 flex flex-col bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 pt-4 pb-3 shrink-0 border-b border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
              Søk etter utstyr
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Søk etter fullt SN"
                className="flex-1 px-4 py-3 text-base border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] bg-white transition-colors min-h-[48px]"
              />
              <button
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
                className="px-5 py-3 bg-[#1E3A5F] text-white text-sm font-semibold rounded-md hover:bg-[#152C4A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm min-h-[48px]"
              >
                {isSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                Søk
              </button>
              <button
                onClick={() => setIsCreationModalOpen(true)}
                className="px-5 py-3 bg-[#0D9488] text-white text-sm font-semibold rounded-md hover:bg-[#0B7A70] transition-colors flex items-center gap-2 shadow-sm min-h-[48px]"
              >
                <Plus size={18} />
                Nytt utstyr
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5">
            {!hasSearched ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-slate-400">Søk for å finne utstyr</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-slate-500">Ingen resultater for "{searchQuery}"</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {searchResults.map((result) => (
                  <div key={result.id} className="py-3">
                    <button
                      onClick={() => handleResultClick(result)}
                      className="w-full flex items-start justify-between gap-4 hover:bg-slate-50 rounded transition-colors text-left group min-h-[52px] px-1"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-slate-800 group-hover:text-[#1E3A5F] truncate transition-colors">
                          {result.name || 'Uten navn'}
                        </p>
                        <EquipmentSearchMeta item={result} />
                      </div>
                      <p className="text-sm text-slate-400 shrink-0 pt-0.5">
                        {result.company_name}
                      </p>
                    </button>

                    {result.sub_equipment.length > 0 && (
                      <div className="mt-2 ml-3 pl-3 border-l-2 border-slate-200 space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400">
                          <Link2 size={12} className="text-[#0D9488]" />
                          Tilknyttet utstyr ({result.sub_equipment.length})
                        </div>
                        {result.sub_equipment.map((sub) => (
                          <button
                            key={sub.id}
                            onClick={() => handleResultClick(sub)}
                            className="w-full text-left px-1 py-1.5 rounded hover:bg-slate-50 transition-colors group"
                          >
                            <p className="text-sm font-medium text-slate-700 group-hover:text-[#1E3A5F] truncate">
                              {sub.name || 'Uten navn'}
                            </p>
                            <EquipmentSearchMeta item={sub} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

      {selectedEquipment && (
        <EquipmentDetailsModal
          isOpen
          fullscreen
          onClose={() => setSelectedEquipment(null)}
          equipment={{
            id: selectedEquipment.id,
            name: selectedEquipment.name,
            part_number: selectedEquipment.part_number,
            asset_number: selectedEquipment.asset_number,
            serial_number: selectedEquipment.serial_number,
            owner_id: selectedEquipment.owner_id,
            last_inspection_date: null,
            next_inspection_date: null,
            last_ndt_date: selectedEquipment.last_ndt_date,
            next_ndt_date: selectedEquipment.next_ndt_date,
            status: '',
            inspection_status: null,
            description: null,
            model: null,
            manufacture_year: null,
            category_id: null,
            company_id: selectedEquipment.company_id,
          }}
          onCopied={(created) => {
            sessionStorage.setItem('selectedCompanyId', created.company_id || selectedEquipment.company_id)
            setSelectedEquipment({
              id: created.id,
              name: created.name,
              part_number: created.part_number,
              asset_number: created.asset_number,
              serial_number: created.serial_number || '',
              owner_id: created.owner_id,
              last_ndt_date: created.last_ndt_date,
              next_ndt_date: created.next_ndt_date,
              company_id: created.company_id || selectedEquipment.company_id,
              company_name: selectedEquipment.company_name,
            })
          }}
        />
      )}

      <EquipmentCreationModal
        isOpen={isCreationModalOpen}
        onClose={() => setIsCreationModalOpen(false)}
        onSuccess={(created) => {
          sessionStorage.setItem('selectedCompanyId', created.company_id)
          setSelectedEquipment({
            id: created.id,
            name: created.name,
            part_number: created.part_number,
            asset_number: created.asset_number,
            serial_number: created.serial_number,
            owner_id: created.owner_id,
            last_ndt_date: created.last_ndt_date,
            next_ndt_date: created.next_ndt_date,
            company_id: created.company_id,
            company_name: '',
          })
        }}
      />
    </div>
  )
}

export default Dashboard
